import DataTable from "datatables.net";
import 'datatables.net-bs5';
import * as Highcharts from "highcharts";
import {get, init_tooltips, notifier, on, val} from "./util";
import {Order} from "datatables.net-bs5";
import { getChartTheme } from './utils/chartTheme';

declare var manga_monthly: Array<{ month: string; count: number }>;
declare var manga_weekly_pace: Array<{ week: string; chapters: number }>;

export default class {
    static init = () => {
        manga();
        reads();
        stats();
    }
}

// ── Stats page ──────────────────────────────────────────────────────────────

function renderSparkline(id: string, data: number[]): void {
    const el = document.getElementById(id);
    if (!el) return;
    if (!data || data.length === 0) return;

    Highcharts.chart(id, Highcharts.merge(getChartTheme(), {
        chart: {
            type: 'area',
            height: 30,
            margin: [2, 0, 2, 0],
            animation: false,
        },
        title: { text: undefined },
        credits: { enabled: false },
        legend: { enabled: false },
        tooltip: { enabled: false },
        xAxis: {
            visible: false,
        },
        yAxis: {
            visible: false,
            startOnTick: false,
            endOnTick: false,
        },
        plotOptions: {
            area: {
                lineWidth: 1,
                color: '#ffa94d',
                fillOpacity: 0.15,
                marker: { enabled: false },
                animation: false,
            },
        },
        series: [{
            type: 'area',
            data: data,
        }],
    } as any));
}

function renderBadge(selector: string): void {
    const el = document.querySelector(selector) as HTMLElement | null;
    if (!el) return;
    const raw = el.getAttribute('data-change');
    if (raw === null || raw === '') return;

    const value = parseFloat(raw);
    if (isNaN(value)) return;

    const isTime = selector.includes('time');
    let label: string;
    let cls: string;

    if (isTime) {
        const absMinutes = Math.abs(Math.round(value / 60));
        const h = Math.floor(absMinutes / 60);
        const m = absMinutes % 60;
        const parts: string[] = [];
        if (h > 0) parts.push(h + 'h');
        if (m > 0) parts.push(m + 'm');
        const formatted = parts.length > 0 ? parts.join(' ') : '0m';
        label = (value >= 0 ? '+' : '-') + formatted;
        cls = value >= 0 ? 'bg-success' : 'bg-danger';
    } else {
        label = (value >= 0 ? '+' : '') + value;
        cls = value >= 0 ? 'bg-success' : 'bg-danger';
    }

    el.innerHTML = `<span class="badge ${cls}">${label}</span>`;
}

function filterByRange(
    allData: Array<Record<string, any>>,
    range: string | null,
    dateField: string
): Array<Record<string, any>> {
    if (range === '6M' || range === '1Y') {
        const months = range === '6M' ? 6 : 12;
        const cutoff = new Date();
        cutoff.setMonth(cutoff.getMonth() - months);
        return allData.filter((d: Record<string, any>) => new Date(d[dateField]) >= cutoff);
    }
    return allData;
}

function wireRangeButtons(
    selector: string,
    allData: Array<Record<string, any>>,
    chart: Highcharts.Chart,
    dateField: string,
    yField: string
): void {
    const group = document.querySelector(selector);
    if (!group) return;

    group.querySelectorAll('button').forEach((btn) => {
        btn.addEventListener('click', () => {
            group.querySelectorAll('button').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');

            const range = btn.getAttribute('data-range');
            const filtered = filterByRange(allData, range, dateField);

            chart.xAxis[0].setCategories(filtered.map((d: Record<string, any>) => d[dateField]));
            chart.series[0].setData(filtered.map((d: Record<string, any>) => d[yField]));
        });
    });
}

function rollingAverage(
    data: Array<{ week: string; chapters: number }>,
    window: number
): Array<{ week: string; chapters: number }> {
    return data.map((d, i) => {
        const slice = data.slice(Math.max(0, i - window + 1), i + 1);
        const avg = slice.reduce((sum, x) => sum + x.chapters, 0) / slice.length;
        return { week: d.week, chapters: Math.round(avg * 10) / 10 };
    });
}

function stats(): void {
    if (typeof manga_monthly === 'undefined') return;

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
    ];

    function formatMonth(ym: string): string {
        const parts = ym.split('-');
        if (parts.length < 2) return ym;
        const monthIdx = parseInt(parts[1], 10) - 1;
        const year = parts[0];
        return (monthNames[monthIdx] ?? ym) + ' ' + year;
    }

    // ── Sparklines ────────────────────────────────────────────────────────

    if (manga_monthly.length > 0) {
        const last12 = manga_monthly.slice(-12);
        const sparklineChapters = last12.map(d => d.count);
        renderSparkline('sparkline_chapters', sparklineChapters);
    }
    // sparkline_series and sparkline_time: no monthly arrays available — skip gracefully

    // ── Comparison badges ─────────────────────────────────────────────────

    renderBadge('.js-badge-chapters');
    renderBadge('.js-badge-series');
    renderBadge('.js-badge-time');

    // ── Column chart — Chapters read per month ────────────────────────────

    if (manga_monthly.length > 0) {
        const columnChart = Highcharts.chart('chart_manga_monthly', Highcharts.merge(getChartTheme(), {
            chart: { type: 'column', height: 350 },
            title: { text: 'Chapters Read per Month', style: { fontSize: '14px' } },
            xAxis: {
                categories: manga_monthly.map(d => d.month),
                labels: { rotation: -45 },
            },
            yAxis: {
                title: { text: 'Chapters' },
                allowDecimals: false,
                gridLineWidth: 0.1,
            },
            tooltip: {
                formatter: function (this: any): string {
                    const count = this.y ?? 0;
                    const label = formatMonth(this.x);
                    return `<b>${label}</b><br/>${count} chapter${count !== 1 ? 's' : ''} read`;
                },
            },
            legend: { enabled: false },
            credits: { enabled: false },
            series: [{
                type: 'column',
                name: 'Chapters',
                color: '#ffa94d',
                data: manga_monthly.map(d => d.count),
            }],
        } as any));

        wireRangeButtons('.js-range-manga', manga_monthly as any[], columnChart, 'month', 'count');
    }

    // ── Line chart — Reading pace (4-week rolling average) ────────────────

    if (typeof manga_weekly_pace !== 'undefined' && manga_weekly_pace.length > 0) {
        const smoothed = rollingAverage(manga_weekly_pace, 4);

        const paceChart = Highcharts.chart('chart_manga_pace', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 350 },
            title: { text: 'Reading Pace (4-Week Rolling Average)', style: { fontSize: '14px' } },
            xAxis: {
                categories: smoothed.map(d => d.week),
                labels: { rotation: -45 },
                tickAmount: 12,
            },
            yAxis: {
                title: { text: 'Chapters/Week' },
                allowDecimals: true,
                gridLineWidth: 0.1,
            },
            tooltip: {
                formatter: function (this: any): string {
                    const chapters = this.y ?? 0;
                    return `<b>Week of ${this.x}</b><br/>${chapters.toFixed(1)} chapters/week (4-week avg)`;
                },
            },
            legend: { enabled: false },
            credits: { enabled: false },
            plotOptions: {
                line: {
                    marker: { enabled: false },
                },
            },
            series: [{
                type: 'line',
                name: 'Chapters/Week',
                color: '#51cf66',
                data: smoothed.map(d => ({ name: d.week, y: d.chapters })),
            }],
        } as any));

        // Custom range wiring: filter raw data then recompute rolling average
        const paceGroup = document.querySelector('.js-range-pace');
        if (paceGroup) {
            paceGroup.querySelectorAll('button').forEach((btn) => {
                btn.addEventListener('click', () => {
                    paceGroup.querySelectorAll('button').forEach(b => b.classList.remove('active'));
                    btn.classList.add('active');

                    const range = btn.getAttribute('data-range');
                    const filtered = filterByRange(manga_weekly_pace as any[], range, 'week') as Array<{ week: string; chapters: number }>;
                    const reSmoothed = rollingAverage(filtered, 4);

                    paceChart.series[0].setData(reSmoothed.map(d => ({ name: d.week, y: d.chapters })));
                    paceChart.xAxis[0].setCategories(reSmoothed.map(d => d.week));
                });
            });
        }
    }
}

function manga() {
    let table = document.querySelector('.datatable[data-table="manga_list"]') as HTMLElement;
    if(!table) {
        return;
    }

    let datatable = new DataTable('.datatable[data-table="manga_list"]',{
        pageLength: 20,
        lengthMenu: [10,20,50,100],
        processing: true,
        serverSide: true,
        ajax: {
            url: '?do=getManga',
            data: function(data) {
                return data;
            }
        },
        columns: [
            { data: null, render: function(data) {
                return `<span class="table-circle" style="background-color:${data.color}"></span> ${data.name}`;
            }},
            { data: 'author' },
            { data: 'chapters' },
            { data: null, render: function(data) {
                return `<div class="progress" style="width:300px"><div class="progress-bar" role="progressbar" style="width: ${data.progress}%" aria-valuenow="${data.progress}" aria-valuemin="0" aria-valuemax="100"></div><div class="progress-bar-text">${data.progress}%</div></div>`;
            }},
            { data: 'time' },
            { data: 'per_chapter' },
        ],
        order: [[1,"asc"]] as Order[],
        drawCallback: init_tooltips
    });
}

function reads() {
    let table = document.querySelector('.datatable[data-table="reads"]') as HTMLElement;
    if(!table) {
        return;
    }

    let datatable = new DataTable('.datatable[data-table="reads"]',{
        pageLength: 15,
        lengthMenu: [10,15,20,50,100],
        processing: true,
        serverSide: true,
        ajax: {
            url: '?do=getReads',
            data: function (data) {
                return data;
            }
        },
        columns: [
            { data: null, className: 'align-middle', render: function (data, type, row) {
                const pausedBadge = data.is_paused
                    ? `<span class="badge bg-warning text-dark me-1">Paused</span> `
                    : '';
                return `${pausedBadge}<img src="${data.image}" class="img-fluid me-1" style="height: 20px" />&nbsp;${data.name}`;
            }},
            { data: 'saga', className: 'align-middle' },
            { data: 'arc', className: 'align-middle' },
            { data: 'volume', className: 'align-middle' },
            { data: 'chapter', className: 'align-middle' },
            { data: 'time', className: 'align-middle' },
            { data: 'date', className: 'align-middle' }
        ],
        order: [[6,"desc"]] as Order[],
        createdRow: function(row, data : { [key: string]: any }, dataIndex, cells) {
            let element = row as HTMLElement;
            if(data.is_current) {
                element.classList.add('row-current');
            }
            if(data.is_paused) {
                element.classList.add('table-warning');
            }
        }
    });

    let next_manga_id : number;
    let next_manga_chapters_id : number;

    function updateNextRead() {
        get('?do=getNextRead',{
            manga_id: table.getAttribute('data-manga_id') ?? ''
        },(data : {
            manga_id: number,
            manga_chapters_id: number,
            chapter: number, next: string,
            manga_progress: number,
            manga_unread: number,
            manga: string,
            manga_chapters_left: number,
            volume_progress: number,
            volume: string,
            volume_chapters_left: number,
            arc_progress: number,
            arc: string,
            arc_chapters_left: number,
            saga_progress: number,
            saga: string
            saga_chapters_left: number,
            is_started: boolean,
            is_paused: boolean
        }) => {
            next_manga_id = data.manga_id;
            next_manga_chapters_id = data.manga_chapters_id;
            val('[name="chapter"]',data.chapter);
            let next = document.querySelector('.next-up');
            if(next) {
                next.innerHTML = data.next;
            }

            let manga_progress = document.querySelector('.manga-progress') as HTMLElement;
            let manga_progress_text = manga_progress.parentElement?.querySelector('.progress-bar-text') as HTMLElement;
            manga_progress.style.width = (data.manga_progress - data.manga_unread) + '%';
            manga_progress.style.marginLeft = data.manga_unread + '%';
            manga_progress_text.innerHTML = data.manga + " (" + data.manga_progress + '% - ' + data.manga_chapters_left + ' Chapters Left)';

            let volume_progress = document.querySelector('.volume-progress') as HTMLElement;
            let volume_progress_text = volume_progress.parentElement?.querySelector('.progress-bar-text') as HTMLElement;
            volume_progress.style.width = data.volume_progress + '%';
            volume_progress_text.innerHTML = "Volume " + data.volume + " (" + data.volume_progress + '% - ' + data.volume_chapters_left + ' Chapters Left)';

            let arc_progress = document.querySelector('.arc-progress') as HTMLElement;
            let arc_progress_text = arc_progress.parentElement?.querySelector('.progress-bar-text') as HTMLElement;
            arc_progress.style.width = data.arc_progress + '%';
            arc_progress_text.innerHTML = data.arc + " Arc (" + data.arc_progress + '% - ' + data.arc_chapters_left + ' Chapters Left)';

            let saga_progress = document.querySelector('.saga-progress') as HTMLElement;
            let saga_progress_text = saga_progress.parentElement?.querySelector('.progress-bar-text') as HTMLElement;
            saga_progress.style.width = data.saga_progress + '%';
            saga_progress_text.innerHTML = (data.saga !== null ? data.saga + " Saga (" + data.saga_progress + '% - ' + data.saga_chapters_left + ' Chapters Left)' : '');

            if(data.saga !== null) {
                // @ts-ignore
                saga_progress.parentElement.style.maxHeight = '20px';
            } else {
                // @ts-ignore
                saga_progress.parentElement.style.maxHeight = '0';
            }

            let startReadEl = document.querySelector('.js-start-read') as HTMLElement;
            let endReadEl = document.querySelector('.js-end-read') as HTMLElement;
            let pauseReadEl = document.querySelector('.js-pause-read') as HTMLElement;
            let resumeReadEl = document.querySelector('.js-resume-read') as HTMLElement;
            if(data.is_paused) {
                resumeReadEl.style.display = '';
                endReadEl.style.display = 'none';
                startReadEl.style.display = 'none';
                pauseReadEl.style.display = 'none';
            } else if(data.is_started) {
                endReadEl.style.display = '';
                pauseReadEl.style.display = '';
                startReadEl.style.display = 'none';
                resumeReadEl.style.display = 'none';
            } else {
                startReadEl.style.display = '';
                endReadEl.style.display = 'none';
                pauseReadEl.style.display = 'none';
                resumeReadEl.style.display = 'none';
            }
        });
    }

    updateNextRead();

    on('click','.js-start-read',(start_read : HTMLElement) => {
        let end_read = document.querySelector('.js-end-read') as HTMLElement;
        const pause_read = document.querySelector('.js-pause-read') as HTMLElement;
        const resume_read = document.querySelector('.js-resume-read') as HTMLElement;
        start_read.classList.add('disabled');

        if(next_manga_id && next_manga_chapters_id) {
            get('?do=startRead',{
                next_manga_id: next_manga_id,
                manga_chapters_id: next_manga_chapters_id
            },(data : any) => {
                if(data) {
                    start_read.classList.remove('disabled');
                    start_read.style.display = 'none';
                    end_read.style.display = '';
                    pause_read.style.display = '';
                    resume_read.style.display = 'none';
                    datatable.ajax.reload();
                }
            });
        }
    });

    on('click','.js-end-read',(end_read : HTMLElement) => {
        let start_read = document.querySelector('.js-start-read') as HTMLElement;
        end_read.classList.add('disabled');

        if(next_manga_id && next_manga_chapters_id) {
            get('?do=endRead',{
                next_manga_id: next_manga_id,
                manga_chapters_id: next_manga_chapters_id
            },(data : any) => {
                if(data) {
                    end_read.classList.remove('disabled');
                    end_read.style.display = 'none';
                    start_read.style.display = '';
                    datatable.ajax.reload();
                    updateNextRead();
                }
            });
        }
    });

    on('click','.js-pause-read',(pause_read : HTMLElement) => {
        let resume_read = document.querySelector('.js-resume-read') as HTMLElement;
        let end_read = document.querySelector('.js-end-read') as HTMLElement;
        pause_read.classList.add('disabled');

        if(next_manga_id && next_manga_chapters_id) {
            get('?do=pauseManga',{
                next_manga_id: next_manga_id,
                manga_chapters_id: next_manga_chapters_id
            },(data : any) => {
                pause_read.classList.remove('disabled');
                if(data && data.status === 1) {
                    pause_read.style.display = 'none';
                    end_read.style.display = 'none';
                    resume_read.style.display = '';
                    datatable.ajax.reload();
                    updateNextRead();
                } else {
                    notifier.alert('Failed to pause manga');
                }
            });
        }
    });

    on('click','.js-resume-read',(resume_read : HTMLElement) => {
        let pause_read = document.querySelector('.js-pause-read') as HTMLElement;
        let end_read = document.querySelector('.js-end-read') as HTMLElement;
        resume_read.classList.add('disabled');

        if(next_manga_id && next_manga_chapters_id) {
            get('?do=resumeManga',{
                next_manga_id: next_manga_id,
                manga_chapters_id: next_manga_chapters_id
            },(data : any) => {
                resume_read.classList.remove('disabled');
                if(data && data.status === 1) {
                    resume_read.style.display = 'none';
                    pause_read.style.display = '';
                    end_read.style.display = '';
                    datatable.ajax.reload();
                    updateNextRead();
                } else {
                    notifier.alert('Failed to resume manga');
                }
            });
        }
    });
}

function next() {
}
